import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  LogOut,
  Settings as SettingsIcon,
  Shield,
} from "lucide-react";
import { loadSession } from "../lib/auth";
import { logout } from "../lib/api";

export function AppShell() {
  const session = loadSession();
  const navigate = useNavigate();
  const name = session?.user.name || session?.user.email || "";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/85 backdrop-blur-sm">
        <div className="mx-auto flex h-14 max-w-screen-2xl items-center gap-3 px-3 sm:gap-6 sm:px-6 lg:px-8">
          <Link
            to="/app/dashboard"
            className="flex items-center gap-2 font-semibold tracking-tight text-brand"
          >
            <Logo />
            <span className="hidden xs:inline sm:inline">AuftragsManager</span>
          </Link>
          <nav className="flex items-center gap-1 text-sm">
            <NavItem to="/app/dashboard" icon={<LayoutDashboard className="h-4 w-4" />}>
              Dashboard
            </NavItem>
            {session?.user.role === "admin" && (
              <NavItem to="/app/settings" icon={<SettingsIcon className="h-4 w-4" />}>
                Einstellungen
              </NavItem>
            )}
            {session?.user.role === "superadmin" && (
              <NavItem to="/master" icon={<Shield className="h-4 w-4" />}>
                Master
              </NavItem>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                {initials(name)}
              </div>
              <span className="hidden text-slate-700 sm:inline">{name}</span>
            </div>
            <button
              className="btn-ghost btn-sm"
              title="Abmelden"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Abmelden</span>
            </button>
          </div>
        </div>
      </header>
      <main className="page">
        <Outlet />
      </main>
    </div>
  );
}

function NavItem({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `inline-flex items-center gap-2 rounded-lg px-3 py-2 transition-colors sm:py-1.5 ${
          isActive
            ? "bg-slate-100 font-medium text-slate-900"
            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        }`
      }
    >
      {icon}
      <span className="hidden sm:inline">{children}</span>
    </NavLink>
  );
}

function Logo() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="32" height="32" rx="8" fill="currentColor" />
      <path
        d="M9 22V10h6.5c2.5 0 4 1.4 4 3.5 0 1.7-1 2.9-2.6 3.3l3.1 5.2H17l-2.8-5h-2.7v5H9zm2.5-7h3.7c1.3 0 2.1-.6 2.1-1.7 0-1-.8-1.7-2.1-1.7h-3.7V15z"
        fill="white"
      />
    </svg>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
